import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DICTIONARIES, DEFAULT_LOCALE, SUPPORTED_LOCALES } from "./i18n_dict";

const I18nCtx = createContext(null);

const STORAGE_KEY = "cibraxis_locale";

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
    // Refresh document lang for a11y / spellcheck
    document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key, vars) => {
      const dict = DICTIONARIES[locale] || DICTIONARIES[DEFAULT_LOCALE];
      const fallback = DICTIONARIES[DEFAULT_LOCALE];
      let str = dict[key] ?? fallback[key] ?? key;
      if (vars && typeof str === "string") {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp(`{${k}}`, "g"), String(v));
        }
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t, locales: SUPPORTED_LOCALES }), [locale, setLocale, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useT = () => {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useT must be used inside <I18nProvider>");
  return ctx;
};
